/**
 * Browser-side error reporting: crashes and infrastructure-level API failures go to
 * POST /api/client-errors, where the API logs them for Error Reporting and alerts on
 * them. Fire-and-forget, never throws, one report per message per 30 s so a render loop
 * (or a failing poll) cannot flood anything.
 */
export interface ClientErrorReport {
  message: string;
  stack?: string;
  componentStack?: string;
  source: 'boundary' | 'window' | 'promise' | 'trpc';
}

const recent = new Map<string, number>();
const DEDUPE_MS = 30_000;

export function reportClientError(report: ClientErrorReport): void {
  const now = Date.now();
  const last = recent.get(report.message);
  if (last !== undefined && now - last < DEDUPE_MS) return;
  recent.set(report.message, now);
  try {
    const body = JSON.stringify({ ...report, message: report.message.slice(0, 2000), url: window.location.href });
    void fetch('/api/client-errors', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      credentials: 'same-origin',
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // reporting must never become the next error
  }
}

function describe(reason: unknown): { message: string; stack?: string } {
  if (reason instanceof Error) return { message: reason.message || reason.name, stack: reason.stack };
  return { message: typeof reason === 'string' ? reason : JSON.stringify(reason) };
}

/** Catch what no component handles: uncaught exceptions and unhandled promise rejections. */
export function installGlobalErrorReporting(): void {
  window.addEventListener('error', (event) => {
    const { message, stack } = describe(event.error ?? event.message);
    reportClientError({ message, stack, source: 'window' });
  });
  window.addEventListener('unhandledrejection', (event) => {
    const { message, stack } = describe(event.reason);
    reportClientError({ message, stack, source: 'promise' });
  });
}
